import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Delete, Trash2, Volume2, Mail, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { ActionBarSide, ActionButtonDef, getOrderedEnabledActions, splitActionGroups } from "@/components/aac/ActionBarRenderer";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { getPredictionEngine } from "@/lib/aacPredictionEngine";
import { scheduleSyncToCloud } from "@/lib/aacLearningSync";
import { speak } from "@/lib/ttsService";
import { loadKeyboardConfig, KeyboardConfig } from "@/lib/keyboardConfigTypes";
import { getUserSettings } from "@/lib/userSettings";

import { supabase } from "@/integrations/supabase/client";
import { useCaregiverContacts } from "@/hooks/useCaregiverContacts";
import { CursorStore } from "@/features/cursor/CursorStore";

// Keyboard layouts - natural keyboard style
const hebrewLayout = [
  ["ק", "ר", "א", "ט", "ו", "ן", "ם", "פ"],
  ["ש", "ד", "ג", "כ", "ע", "י", "ח", "ל", "ך", "ף"],
  ["ז", "ס", "ב", "ה", "נ", "מ", "צ", "ת", "ץ"],
];

const englishLayout = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
];

const russianLayout = [
  ["Й", "Ц", "У", "К", "Е", "Н", "Г", "Ш", "Щ", "З", "Х", "Ъ"],
  ["Ф", "Ы", "В", "А", "П", "Р", "О", "Л", "Д", "Ж", "Э", "Ё"],
  ["Я", "Ч", "С", "М", "И", "Т", "Ь", "Б", "Ю"],
];

const arabicLayout = [
  ["ض", "ص", "ث", "ق", "ف", "غ", "ع", "ه", "خ", "ح"],
  ["ش", "س", "ي", "ب", "ل", "ا", "ت", "ن", "م", "ك"],
  ["ئ", "ء", "ؤ", "ر", "ى", "ة", "و", "ز", "د", "ج"],
];

const numberLayout = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  [".", "0", "%"],
];

// Extra character to show next to backspace in letters mode (question mark)
const LETTERS_MODE_EXTRA_CHAR = "?";
// Extra character to show next to backspace in numbers mode (percent)
const NUMBERS_MODE_EXTRA_CHAR = "%";

const symbolLayout = [
  ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"],
  ["-", "_", "=", "+", "[", "]", "{", "}", "\\", "|"],
  [";", ":", "'", '"', ",", ".", "<", ">", "/", "?"],
];

// Quick phrases are now loaded from keyboardPhrasesTypes config
// See loadPhrasesConfig() and getEnabledPhrases()

// All emojis in one big array
const allEmojis = [
  "😊", "😀", "😃", "😄", "😁", "😅", "😂", "🤣", "😇", "😉", "😍", "🥰", "😘", "😋", "😎",
  "🥺", "😢", "😭", "😤", "😠", "🤔", "🤗", "🤭", "🤫", "🤐", "😴", "🥱", "😷", "🤒", "🤕",
  "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💕", "💞", "💓", "💗", "💖", "💝", "💘",
  "👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "🤙", "👋", "🤚", "✋", "🖐️", "👏", "🙌", "🙏",
  "💪", "🤝", "👊", "✊", "🤜", "🤛", "👆", "👇", "👈", "👉", "☝️", "🖕", "🤌", "🫶", "🫰",
  "👶", "👦", "👧", "👨", "👩", "👴", "👵", "🧔", "👱", "👸", "🤴", "👼", "🎅", "🤶", "🦸",
  "🌸", "🌺", "🌻", "🌹", "🌷", "🌼", "🌿", "🍀", "🌳", "🌴", "🌵", "☀️", "🌙", "⭐", "🌈",
  "🔥", "💧", "❄️", "🌊", "⚡", "☁️", "🌤️", "🌧️", "🌪️", "🌻", "🐶", "🐱", "🐭", "🐰", "🦊",
  "🍎", "🍊", "🍋", "🍇", "🍓", "🍒", "🍑", "🥝", "🍕", "🍔", "🍟", "☕", "🍵", "🍰", "🎂",
  "🍺", "🍷", "🥤", "🧃", "🍦", "🍩", "🍪", "🥐", "🥖", "🍞", "🥚", "🧀", "🥗", "🍝", "🍜",
];

type Language = "hebrew" | "english" | "arabic" | "russian";
type KeyboardMode = "letters" | "numbers" | "symbols" | "emoji" | "acronym";
type AutocompleteMode = "standard" | "acronym";

// Multi-language label map for keyboard UI elements
const KB_LABELS: Record<string, Record<string, string>> = {
  deleteWord:  { he: "מחק מילה", en: "Del Word", ru: "Удал. слово", ar: "حذف كلمة" },
  speak:       { he: "דיבור", en: "Speak", ru: "Озвучить", ar: "نطق" },
  clear:       { he: "נקה", en: "Clear", ru: "Очистить", ar: "مسح" },
  board:       { he: "לוח תקשורת", en: "Board", ru: "Доска", ar: "لوحة التواصل" },
  send:        { he: "שליחה", en: "Send", ru: "Отправить", ar: "إرسال" },
  deleteChar:  { he: "מחק תו", en: "Del Char", ru: "Удал. симв.", ar: "حذف حرف" },
  space:       { he: "רווח", en: "Space", ru: "Пробел", ar: "مسافة" },
  startTyping: { he: "התחילו להקליד...", en: "Start typing...", ru: "Начните печатать...", ar: "ابدأ بالكتابة" },
};

interface Suggestion {
  text: string;
  type: "word" | "phrase";
  acronym?: string;
  isNextWord?: boolean; // true if this is a next-word prediction (append), false if completion (replace)
  isPhrasePair?: boolean; // true if this is a multi-word phrase suggestion (e.g., "מרגיש שמח")
}

interface AACKeyboardProps {
  value: string;
  onChange: (value: string) => void;
  onDone?: () => void;
  onSend?: () => void;
  className?: string;
  uiLanguage?: string;
  hideToolbar?: boolean;
  hideSpeakButton?: boolean;
  sendOnly?: boolean;
}

export function AACKeyboard({
  value,
  onChange,
  onDone,
  onSend,
  className,
  uiLanguage = "he",
  hideToolbar = false,
  hideSpeakButton = false,
  sendOnly = false,
}: AACKeyboardProps) {
  const navigate = useNavigate();
  const isUIHebrew = uiLanguage === "he" || uiLanguage === "ar";
  const kbLabel = (key: string) => KB_LABELS[key]?.[uiLanguage] || KB_LABELS[key]?.en || key;
  const [language, setLanguage] = useState<Language>(uiLanguage === "he" ? "hebrew" : uiLanguage === "ar" ? "arabic" : uiLanguage === "ru" ? "russian" : "english");
  const [mode, setMode] = useState<KeyboardMode>("letters");
  const [autocompleteMode, setAutocompleteMode] = useState<AutocompleteMode>("standard");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [emojiPage, setEmojiPage] = useState(0); // Pagination for emojis
  const previousWordRef = useRef<string | null>(null);
  const engineRef = useRef(getPredictionEngine());
  // Ref to track latest value to avoid stale closures
  const valueRef = useRef(value);
  valueRef.current = value;
  // Ref to the first focusable key in the current mode (for VirtualCursor reset)
  const firstModeKeyRef = useRef<HTMLButtonElement | null>(null);
  // Ref to store TTS-specific text when phrases have different pronunciation
  const ttsTextRef = useRef<string | null>(null);
  
  // Load keyboard config and listen for changes from editor
  const [kbConfig, setKbConfig] = useState<KeyboardConfig>(() => loadKeyboardConfig());
  
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "keyboardConfig_v1") {
        setKbConfig(loadKeyboardConfig());
      }
    };
    // Reload config when returning to this page (same-tab navigation from editor)
    const handleFocus = () => setKbConfig(loadKeyboardConfig());
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);
  
  // Helper to check if a toolbar/action item is enabled in config
  const isToolbarEnabled = useCallback((id: string) => {
    const item = kbConfig.toolbarItems.find(i => i.id === id);
    return item ? item.enabled : true;
  }, [kbConfig]);
  
  const isActionEnabled = useCallback((id: string) => {
    const item = kbConfig.actionItems.find(i => i.id === id);
    return item ? item.enabled : true;
  }, [kbConfig]);

  // Set up cloud sync callback when user is logged in
  useEffect(() => {
    const engine = engineRef.current;
    
    // Check if user is logged in and set up sync callback
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        engine.setOnChangeCallback(() => {
          scheduleSyncToCloud(session.user.id, () => engine.getLearnedData());
        });
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        engine.setOnChangeCallback(() => {
          scheduleSyncToCloud(session.user.id, () => engine.getLearnedData());
        });
      } else {
        engine.setOnChangeCallback(null);
      }
    });

    return () => {
      subscription.unsubscribe();
      engine.setOnChangeCallback(null);
    };
  }, []);
  
  // Get current layout based on mode
  const getActiveLayout = () => {
    if (mode === "letters") {
      if (language === "hebrew") return hebrewLayout;
      if (language === "arabic") return arabicLayout;
      if (language === "russian") return russianLayout;
      return englishLayout;
    }
    if (mode === "numbers") return numberLayout;
    if (mode === "symbols") return symbolLayout;
    if (language === "hebrew") return hebrewLayout;
    if (language === "arabic") return arabicLayout;
    if (language === "russian") return russianLayout;
    return englishLayout;
  };
  
  const activeLayout = getActiveLayout();
  const isEnglishKeyboard = language === "english" && mode === "letters";
  const showKeyboard = mode === "letters" || mode === "numbers" || mode === "symbols" || mode === "acronym";
  const showEmoji = mode === "emoji";

  // Grid dimensions for emoji mode - fully dynamic by available grid size
  const emojiRowsPerPage = 4;
  const EMOJI_MIN_COLUMNS = 5;
  const EMOJI_MAX_COLUMNS = 10;
  const EMOJI_GAP_FALLBACK_PX = 4;
  const [emojiColumns, setEmojiColumns] = useState(10);
  const emojiGridRef = useRef<HTMLDivElement | null>(null);

  const getKeyboardGapPx = useCallback(() => {
    if (typeof window === "undefined") return EMOJI_GAP_FALLBACK_PX;

    const gridEl = emojiGridRef.current;
    if (!gridEl) return EMOJI_GAP_FALLBACK_PX;

    const keyboardRoot = (gridEl.closest(".keyboard-container") as HTMLElement | null) ?? gridEl;
    const rawGap = window.getComputedStyle(keyboardRoot).getPropertyValue("--kb-key-gap").trim();
    const parsedGap = Number.parseFloat(rawGap);

    return Number.isFinite(parsedGap) && parsedGap > 0 ? parsedGap : EMOJI_GAP_FALLBACK_PX;
  }, []);

  useEffect(() => {
    if (!showEmoji) return;

    let animationFrameId: number | null = null;
    const recalcEmojiColumns = () => {
      const gridEl = emojiGridRef.current;
      if (!gridEl) return;

      const { width, height } = gridEl.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;

      const gapPx = getKeyboardGapPx();
      const availableHeight = Math.max(height - gapPx * (emojiRowsPerPage - 1), 1);
      const rowHeight = availableHeight / emojiRowsPerPage;
      const fitColumns = Math.floor((width + gapPx) / (rowHeight + gapPx));
      const nextColumns = Math.max(EMOJI_MIN_COLUMNS, Math.min(EMOJI_MAX_COLUMNS, fitColumns || EMOJI_MIN_COLUMNS));

      setEmojiColumns((prev) => (prev === nextColumns ? prev : nextColumns));
    };

    const scheduleRecalc = () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(recalcEmojiColumns);
    };

    scheduleRecalc();
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleRecalc);
      if (emojiGridRef.current) observer.observe(emojiGridRef.current);
    }
    window.addEventListener("resize", scheduleRecalc);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleRecalc);
    };
  }, [showEmoji, getKeyboardGapPx]);

  const emojisPerPage = emojiColumns * emojiRowsPerPage;
  const totalEmojiPages = Math.ceil(allEmojis.length / emojisPerPage);

  useEffect(() => {
    setEmojiPage((prev) => Math.min(prev, Math.max(totalEmojiPages - 1, 0)));
  }, [totalEmojiPages]);

  const currentPageEmojis = allEmojis.slice(emojiPage * emojisPerPage, (emojiPage + 1) * emojisPerPage);
  const emojiRowCount = Math.ceil(currentPageEmojis.length / emojiColumns);
  
  
  // Emoji pagination - calculate which arrows are visible based on current page and language
  // In RTL (Hebrew): left = next, right = prev
  // In LTR (English): left = prev, right = next
  const canGoPrev = emojiPage > 0;
  const canGoNext = emojiPage < totalEmojiPages - 1;
  // Show left arrow: in RTL if can go next, in LTR if can go prev
  const showLeftArrow = isUIHebrew ? canGoNext : canGoPrev;
  // Show right arrow: in RTL if can go prev, in LTR if can go next
  const showRightArrow = isUIHebrew ? canGoPrev : canGoNext;




  // Reset VirtualCursor to first key when mode or language changes (spatial 5-gesture mode)
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = firstModeKeyRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
        }
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [mode, language]);

  // Update suggestions: immediate local + debounced AI
  useEffect(() => {
    const engine = engineRef.current;
    
    // Don't show suggestions when the field is empty
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    if (autocompleteMode === "acronym" || mode === "acronym") {
      const currentWord = value.trim().split(/\s+/).pop() || "";
      const results = engine.searchAcronym(currentWord);
      setSuggestions(
        results.map((r) => ({
          text: r.phrase,
          type: "phrase" as const,
          acronym: r.acronym,
        }))
      );
      return; // No AI predictions for acronym mode
    }

    // 1. Immediate local predictions via getSuggestions (N-gram backoff)
    const localResults = engine.getSuggestions(value);
    const localSuggestions: Suggestion[] = localResults.map((r) => ({
      text: r.word,
      type: "word" as const,
      isNextWord: r.isNextWord,
      isPhrasePair: r.isPhrasePair,
    }));
    setSuggestions(localSuggestions);

    // 2. Debounced AI predictions (600ms) – only if 3+ words typed
    const wordCount = value.trim().split(/\s+/).length;
    const debounceTimer = setTimeout(async () => {
      if (wordCount >= 3) {
        const aiResults = await engine.fetchSmartPredictions(value);
        if (aiResults.length > 0) {
          // Merge: AI suggestions first, then local, deduplicated, max 6
          const seen = new Set<string>();
          const merged: Suggestion[] = [];

          for (const ai of aiResults) {
            if (!seen.has(ai.word)) {
              seen.add(ai.word);
              merged.push({
                text: ai.word,
                type: "word" as const,
                isNextWord: true,
              });
            }
          }
          for (const local of localSuggestions) {
            if (!seen.has(local.text)) {
              seen.add(local.text);
              merged.push(local);
            }
          }

          setSuggestions(merged.slice(0, 6));
        }
      }
    }, 600);

    return () => clearTimeout(debounceTimer);
  }, [value, autocompleteMode, mode]);

  const handleKeyPress = (key: string) => {
    onChange(value + key);
  };

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
    ttsTextRef.current = null; // Clear TTS text on manual edit
  };

  const handleDeleteWord = () => {
    const trimmed = value.trimEnd();
    const lastSpaceIndex = trimmed.lastIndexOf(" ");
    if (lastSpaceIndex >= 0) {
      onChange(trimmed.substring(0, lastSpaceIndex + 1));
    } else {
      onChange("");
    }
    ttsTextRef.current = null; // Clear TTS text on manual edit
  };

  const handleSpace = () => {
    const words = value.trim().split(/\s+/);
    if (words.length > 0) {
      previousWordRef.current = words[words.length - 1];
    }
    onChange(value + " ");
  };

  const handleClear = () => {
    onChange("");
    setSuggestions([]);
    previousWordRef.current = null;
    ttsTextRef.current = null; // Clear TTS text when clearing input
  };


  const handleEmoji = (emoji: string) => {
    onChange(value + emoji);
    // Clear TTS ref when adding emoji
    ttsTextRef.current = null;
  };


  const handleSpeak = () => {
    if (value) {
      // Use TTS-specific text if available, otherwise use display text
      const textToSpeak = ttsTextRef.current || value;
      speak({
        text: textToSpeak,
        onEnd: () => {
          onChange("");
          ttsTextRef.current = null;
        },
      });
    }
  };

  // Check if there are registered caregivers
  const { hasContacts: hasPhoneContacts } = useCaregiverContacts();
  const canSend = value.trim().length > 0 && hasPhoneContacts;

  const handleSendMessage = () => {
    if (value.trim() && hasPhoneContacts) {
      if (onSend) {
        onSend();
      } else {
        sessionStorage.setItem("sendMessageText", value);
        // Navigate to unified send flow
        navigate("/aac-keyboard/send-flow", { state: { text: value } });
      }
    }
  };

  // Navigate to Communication Board with current text preserved
  const handleGoToCommunicationBoard = () => {
    if (value.trim()) {
      navigate("/communication", { state: { text: value } });
    }
  };

  const handleSelectSuggestion = useCallback((suggestion: Suggestion) => {
    const engine = engineRef.current;
    const currentValue = valueRef.current;
    
    if (suggestion.type === "phrase" && suggestion.acronym) {
      // Acronym phrase: replace current partial word with the phrase
      engine.onPhraseSelected(suggestion.text, suggestion.acronym);
      const words = currentValue.trim().split(/\s+/);
      words.pop();
      const newValue = words.length > 0 
        ? words.join(" ") + " " + suggestion.text + " "
        : suggestion.text + " ";
      onChange(newValue);
    } else if (suggestion.isNextWord) {
      // Next-word prediction: APPEND the word instead of replacing
      // inputBuffer should be the current text for learning context
      engine.onWordSelected(suggestion.text, currentValue);
      // Just append the word to the current text
      const trimmedValue = currentValue.trimEnd();
      onChange(trimmedValue + " " + suggestion.text + " ");
      previousWordRef.current = suggestion.text;
    } else {
      // Completion: REPLACE the current partial word
      // inputBuffer should be the current text for learning context
      engine.onWordSelected(suggestion.text, currentValue);
      const trimmedValue = currentValue.trimEnd();
      const lastSpaceIndex = trimmedValue.lastIndexOf(" ");
      const baseText = lastSpaceIndex >= 0 
        ? trimmedValue.substring(0, lastSpaceIndex + 1) 
        : "";
      onChange(baseText + suggestion.text + " ");
      previousWordRef.current = suggestion.text;
    }
    setSuggestions([]);
  }, [onChange]);

  // Toggle mode - clicking same mode returns to letters
  const toggleMode = (newMode: KeyboardMode) => {
    if (mode === newMode) {
      setMode("letters");
      if (newMode === "acronym") {
        setAutocompleteMode("standard");
      }
    } else {
      setMode(newMode);
      if (newMode === "acronym") {
        setAutocompleteMode("acronym");
        // Acronym mode always uses user's language (Hebrew for Hebrew users)
        // Switch back to user's language from English
        if (language === "english") {
          setLanguage(nativeLanguage);
        }
      } else if (mode === "acronym") {
        setAutocompleteMode("standard");
      }
    }
  };

  // Get native language label for the toggle button
  const getNativeLanguageLabel = (): string => {
    switch (uiLanguage) {
      case "he": return "עברית";
      case "ar": return "عربية";
      case "ru": return "Русский";
      default: return "EN";
    }
  };

  // Get native Language type from uiLanguage (supports all 4 languages)
  const nativeLanguage: Language = (() => {
    switch (uiLanguage) {
      case "en": return "english";
      case "ar": return "arabic";
      case "ru": return "russian";
      default: return "hebrew";
    }
  })();

  const isInLettersMode = mode === "letters";

  // Language toggle label:
  // - In letters mode: show destination language ("לאן תעבור")
  // - In other modes: show current language ("איפה אתה עכשיו")
  const languageToggleLabel = isInLettersMode
    ? (language === "english" ? getNativeLanguageLabel() : "EN")
    : (language === "english" ? "EN" : getNativeLanguageLabel());

  // Handle language button click:
  // - In letters mode: switch language
  // - In other modes: return to letters in the same language (no language switch)
  const handleLanguageButtonClick = () => {
    setMode("letters");
    setAutocompleteMode("standard");
    if (isInLettersMode) {
      setLanguage(prev => prev === "english" ? nativeLanguage : "english");
    }
  };

  // Toolbar items - filtered by keyboard config
  const allToolbarItems = [
    { id: "emoji" as KeyboardMode, label: "😊" },
    { id: "numbers" as KeyboardMode, label: "123" },
    { id: "symbols" as KeyboardMode, label: "@" },
  ];
  const toolbarItems = allToolbarItems.filter(item => isToolbarEnabled(item.id));

  // Build ordered action button definitions from config
  const actionButtonDefs = React.useMemo((): ActionButtonDef[] => {
    const orderedIds = getOrderedEnabledActions(kbConfig.actionItems);
    
    const defsMap: Record<string, () => ActionButtonDef> = {
      deleteWord: () => ({ id: "deleteWord", icon: Delete, label: kbLabel("deleteWord"), onClick: handleDeleteWord, disabled: !value, variant: "default", iconClassName: "text-destructive" }),
      speak: () => ({ id: "speak", icon: Volume2, label: kbLabel("speak"), onClick: handleSpeak, disabled: !value.trim(), variant: "primary", scanCursorTarget: "kb-first-key" }),
      clear: () => ({ id: "clear", icon: Trash2, label: kbLabel("clear"), onClick: handleClear, disabled: !value, variant: "destructive" }),
      pageToggle: () => ({ id: "pageToggle", icon: MessageSquare, label: kbLabel("board"), onClick: handleGoToCommunicationBoard, disabled: !value.trim(), variant: "default" }),
      send: () => ({ id: "send", icon: Mail, label: kbLabel("send"), onClick: handleSendMessage, disabled: !canSend, variant: "default" }),
    };
    
    return orderedIds
      .filter(id => {
        if (id === "speak" && (hideSpeakButton || sendOnly)) return false;
        return true;
      })
      .map(id => defsMap[id]?.())
      .filter(Boolean) as ActionButtonDef[];
  }, [kbConfig, value, canSend, hideSpeakButton, sendOnly, uiLanguage]);
  
  // Split action buttons into two groups for left/right of text field
  const { primarySide: primaryActions, secondarySide: secondaryActions } = React.useMemo(
    () => splitActionGroups(actionButtonDefs.map(d => d.id)),
    [actionButtonDefs]
  );
  
  const primaryActionDefs = actionButtonDefs.filter(d => primaryActions.includes(d.id));
  const secondaryActionDefs = actionButtonDefs.filter(d => secondaryActions.includes(d.id));


  return (
    <div className={cn("flex-1 flex flex-col keyboard-container pt-1", className)}>
      {/* Text Input Area with Action Buttons - Fixed size matching sidebar */}
    <header
      data-scan-row="kb-actions"
      data-scan-cursor-id="kb-actions-row"
      data-scan-cursor-level="row"
      className={cn(
        "flex items-stretch gap-2 mb-2 mt-2 px-4 py-2 rounded-xl",
          "flex-row"
        )}
        dir={isUIHebrew ? "rtl" : "ltr"}
      >
        {/* Primary side (right in Hebrew/Arabic, left in English) */}
        <ActionBarSide
          buttons={primaryActionDefs}
          isRTL={isUIHebrew}
        />

        {/* Text Display - Same height as header buttons */}
        <div
          data-design-id="aac-text-display"
          className="flex-1 px-4 rounded-xl bg-card border-2 border-border flex items-center overflow-y-auto overflow-x-hidden min-h-[76px]"
          dir={value ? (language === "hebrew" ? "rtl" : "ltr") : (isUIHebrew ? "rtl" : "ltr")}
        >
          <div className="w-full text-start">
            {value ? (
              <span className="sm:text-2xl font-medium whitespace-pre-wrap break-words leading-relaxed" style={{ fontSize: '1.25em' }}>
                {value}
              </span>
            ) : (
              <span className="text-muted-foreground text-base sm:text-lg">
                {kbLabel("startTyping")}
              </span>
            )}
          </div>
        </div>

        {/* Secondary side (left in Hebrew/Arabic, right in English) */}
        <ActionBarSide
          buttons={secondaryActionDefs}
          isRTL={isUIHebrew}
        />
      </header>

      {/* Keyboard content with padding */}
      <div className="flex-1 flex flex-col px-4 pb-2 overflow-x-hidden overflow-y-clip min-h-0">
        {/* Suggestions Row - Only show in letters/symbols/acronym mode (not in emoji/phrases/numbers) */}
        {/* In Hebrew (RTL): higher frequency words appear on the RIGHT */}
        {/* In English (LTR): higher frequency words appear on the LEFT */}
        {showKeyboard && mode !== "numbers" && (
          <div
            data-scan-row="kb-suggestions"
            className={cn(
              "flex items-center justify-center gap-2 px-2 mt-1 rounded-xl flex-shrink-0",
              suggestions.length > 0 ? "bg-[hsl(185_30%_90%)] dark:bg-secondary border border-[hsl(185_30%_80%)] dark:border-border" : "bg-muted/40 border border-border/40"
            )}
            dir="ltr"
            style={{
              height: 'clamp(48px, 8vh, 72px)',
            }}
          >
            {/* Reverse order for Hebrew so highest frequency appears on the right */}
            {(language === "hebrew" ? [...suggestions.slice(0, 5)].reverse() : suggestions.slice(0, 5)).map((suggestion, index) => {
              const originalIndex = language === "hebrew"
                ? Math.min(4, suggestions.length - 1) - index
                : index;
              return (
              <motion.button
                  key={`${suggestion.text}-${originalIndex}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleSelectSuggestion(suggestion);
                  }}
                  type="button"
                  data-scan-item
                  data-scan-cursor-id={originalIndex === 0 ? "kb-first-suggestion" : undefined}
                  {...(originalIndex === 0 ? { 'data-scan-cursor-level': 'row' } : {})}
                  data-scan-cursor-target={getUserSettings().scanMode === 'cell_by_cell' ? "action-speak" : "kb-actions-row"}
                  className={cn(
                    "px-4 py-2 rounded-xl font-medium transition-all h-full",
                    "bg-card text-foreground hover:bg-muted/80 border-[1.5px] border-primary/30 shadow-[0_1px_3px_hsl(195_100%_11%/0.08)]",
                    "flex-1 max-w-[180px] flex items-center justify-center"
                  )}
                  style={{ fontSize: 'var(--kb-font-size)' }}
                >
                  {suggestion.text}
                  {suggestion.acronym && (
                    <span className="text-xs opacity-70 mr-1 ml-1">
                      ({suggestion.acronym})
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>
        )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-visible min-h-0">
        <AnimatePresence mode="wait">
          {/* Letters/Numbers/Symbols/Acronym Mode - Show Keyboard */}
          {showKeyboard && (
            <motion.div
              key="keyboard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col justify-between px-2 py-1 min-h-0"
              style={{ gap: 'var(--kb-key-gap)' }}
            >
              {/* Keyboard Grid - Each row takes flex-1 to fill available space */}
              {(() => {
                const maxCols = Math.max(...activeLayout.map(r => r.length));
                return activeLayout.map((row, rowIndex) => (
                    <div
                      key={rowIndex}
                      data-scan-row={`kb-row-${rowIndex}`}
                      className="flex-1 flex justify-center items-stretch rounded-lg min-h-0"
                      dir="ltr"
                      style={{
                        gap: 'var(--kb-key-gap)',
                      }}
                    >
                      {row.map((key, keyIndex) => {
                        const isFirstKey = rowIndex === 0 && (isUIHebrew ? keyIndex === row.length - 1 : keyIndex === 0);
                        return (
                        <motion.button
                          key={key}
                          ref={(el: HTMLButtonElement | null) => {
                            if (isFirstKey) firstModeKeyRef.current = el;
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleKeyPress(key)}
                          data-scan-item
                          {...(isFirstKey ? { 'data-scan-default': '', 'data-scan-cursor-id': 'kb-first-key', 'data-scan-cursor-level': 'row' } : {})}
                          data-scan-cursor-target="kb-first-suggestion"
                          className={cn(
                            "rounded-xl aspect-square h-full",
                            "bg-card hover:bg-primary/20 border border-[#1E888F] hover:border-primary/50 transition-all",
                            "flex items-center justify-center font-bold text-foreground",
                            isEnglishKeyboard && "font-poppins"
                          )}
                          style={{
                            maxWidth: `${100 / maxCols}%`,
                            fontSize: 'calc(clamp(1.6rem, 3.8vh, 3.5rem) * var(--font-scale, 1) * var(--card-label-scale-cap, 1))',
                          }}
                        >
                          {key}
                        </motion.button>
                        );
                      })}
                    </div>
                ));
              })()}

              {/* Bottom Action Row - Delete Char + Space + Punctuation - Same height as keyboard rows */}
              {/* In Hebrew: punctuation on right, space in middle, delete char on left */}
              {/* In English: delete char on left, space in middle, punctuation on right */}
              {(() => {
                // Define elements in logical order (left-to-right for LTR, will be reversed visually for RTL)
                const deleteCharButton = (
                  <motion.button
                    key="deleteChar"
                    whileTap={{ scale: 0.95 }}
                    onClick={handleBackspace}
                    data-scan-item
                    className="rounded-xl h-full min-w-[clamp(5rem,16vh,16rem)] px-3 bg-card hover:bg-muted/80 border border-[#1E888F] transition-all flex flex-col items-center justify-center gap-1 p-2"
                  >
                    <Delete style={{ width: 'clamp(1.5rem, 3vh, 2.5rem)', height: 'clamp(1.5rem, 3vh, 2.5rem)' }} className="text-destructive flex-shrink-0" />
                    <span style={{ fontSize: 'calc(clamp(0.8rem, 2.5vh, 1.4rem) * var(--font-scale, 1) * var(--card-label-scale-cap, 1))' }} className="font-medium text-foreground whitespace-nowrap">{kbLabel("deleteChar")}</span>
                  </motion.button>
                );

                const spaceButton = (
                  <motion.button
                    key="space"
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSpace}
                    data-scan-item
                    className="flex-1 max-w-[400px] rounded-xl bg-card hover:bg-muted/80 border border-[#1E888F] transition-all flex items-center justify-center font-medium h-full"
                    style={{
                      fontSize: 'calc(clamp(1rem, 2.5vh, 2.2rem) * var(--font-scale, 1) * var(--card-label-scale-cap, 1))',
                    }}
                  >
                    {kbLabel("space")}
                  </motion.button>
                );

                // In letters mode show "?" next to ".", in numbers mode show "%"
                const extraChar = mode === "numbers" ? NUMBERS_MODE_EXTRA_CHAR : LETTERS_MODE_EXTRA_CHAR;
                const punctButtons = (
                  [".", extraChar].map((punct) => (
                    <motion.button
                      key={punct}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleKeyPress(punct)}
                      data-scan-item
                      className={cn(
                        "rounded-xl aspect-square h-full bg-card hover:bg-primary/20 border border-[#1E888F] hover:border-primary/50 transition-all flex items-center justify-center font-bold",
                        "text-[clamp(1.5rem,40%,3rem)]"
                      )}
                    >
                      {punct}
                    </motion.button>
                  ))
                );

                return (
                  <div
                    data-scan-row="kb-bottom"
                    className="flex-1 flex justify-center items-stretch rounded-lg min-h-0"
                    dir="ltr"
                    style={{
                      gap: 'var(--kb-key-gap)',
                    }}
                  >
                    {isUIHebrew ? (
                      <>
                        {/* Hebrew visual order (left to right): Delete Char | Space | (. | % only if not numbers mode) */}
                        {deleteCharButton}
                        {spaceButton}
                        {mode !== "numbers" && punctButtons}
                      </>
                    ) : (
                      <>
                        {/* English visual order (left to right): Delete Char | Space | (. | % only if not numbers mode) */}
                        {deleteCharButton}
                        {spaceButton}
                        {mode !== "numbers" && punctButtons}
                      </>
                    )}
                  </div>
                );
              })()}
            </motion.div>
          )}



          {/* Emoji Mode */}
          {mode === "emoji" && (
            <motion.div
              key="emoji"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex min-h-0 overflow-visible"
              dir="ltr"
            >
              {/* Left pagination arrow - positioned on left side like Communication Board */}
              <div className="flex-shrink-0 w-10 sm:w-12 md:w-16 lg:w-20 h-full flex items-center justify-center" data-scan-row="pagination-prev">
                {showLeftArrow && totalEmojiPages > 1 ? (
                  <Button
                    variant="outline"
                    size="icon"
                    data-scan-item
                    onClick={() => {
                      if (isUIHebrew) {
                        setEmojiPage(prev => Math.min(totalEmojiPages - 1, prev + 1));
                      } else {
                        setEmojiPage(prev => Math.max(0, prev - 1));
                      }
                    }}
                    className={cn(
                      "h-full w-full rounded-2xl border border-[#1E888F] dark:border-[hsl(185_40%_40%)] bg-card",
                      "hover:bg-accent hover:text-accent-foreground",
                      "transition-all duration-200"
                    )}
                  >
                    <ChevronLeft style={{ width: 48, height: 48 }} className="text-foreground !w-12 !h-12" strokeWidth={3} />
                  </Button>
                ) : (
                  <div className="w-full h-full" />
                )}
              </div>

              {/* Emoji grid - fills remaining space */}
              <div
                ref={emojiGridRef}
                className="flex-1 flex flex-col justify-center px-1 pt-1 min-h-0"
                style={{ gap: "var(--kb-key-gap, 4px)" }}
              >
                {/* Build emoji grid in rows - each row gets equal share of available height */}
                {Array.from({ length: emojiRowCount }).map((_, rowIdx) => {
                  const startIdx = rowIdx * emojiColumns;
                  const rowCells = Array.from({ length: emojiColumns }, (_, colIdx) => currentPageEmojis[startIdx + colIdx] ?? null);

                  return (
                    <div
                      key={rowIdx}
                      data-scan-row={`emoji-row-${emojiPage}-${rowIdx}`}
                      className="flex justify-center rounded-lg px-1"
                      style={{
                        flex: `1 1 0`,
                        minHeight: 0,
                        gap: "var(--kb-key-gap, 4px)",
                      }}
                    >
                      {rowCells.map((emoji, colIdx) => {
                        const flatIndex = startIdx + colIdx;

                        return (
                          <div key={`emoji-cell-${flatIndex}-${emojiPage}`} className="flex-1 min-w-0 flex items-center justify-center">
                            {emoji ? (
                              <motion.button
                                key={`${emoji}-${flatIndex}-${emojiPage}`}
                                ref={(el: HTMLButtonElement | null) => {
                                  if (flatIndex === 0) firstModeKeyRef.current = el;
                                }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: Math.min(flatIndex * 0.005, 0.2) }}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleEmoji(emoji)}
                                data-scan-item
                                {...(flatIndex === 0 ? { 'data-scan-default': '', 'data-scan-cursor-id': 'aac-first-emoji', 'data-scan-cursor-level': 'row' } : {})}
                                className="aspect-square rounded-xl bg-card hover:bg-primary/10 border border-[#1E888F] hover:border-primary/50 transition-all flex items-center justify-center text-2xl sm:text-3xl w-full max-h-full cursor-pointer"
                              >
                                {emoji}
                              </motion.button>
                            ) : (
                              <div className="aspect-square w-full max-h-full rounded-xl border-2 border-transparent opacity-0 pointer-events-none" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Right pagination arrow - positioned on right side like Communication Board */}
              <div className="flex-shrink-0 w-10 sm:w-12 md:w-16 lg:w-20 h-full flex items-center justify-center" data-scan-row="pagination-next">
                {showRightArrow && totalEmojiPages > 1 ? (
                  <Button
                    variant="outline"
                    size="icon"
                    data-scan-item
                    onClick={() => {
                      if (isUIHebrew) {
                        setEmojiPage(prev => Math.max(0, prev - 1));
                      } else {
                        setEmojiPage(prev => Math.min(totalEmojiPages - 1, prev + 1));
                      }
                    }}
                    className={cn(
                      "h-full w-full rounded-2xl border border-[#1E888F] dark:border-[hsl(185_40%_40%)] bg-card",
                      "hover:bg-accent hover:text-accent-foreground",
                      "transition-all duration-200"
                    )}
                  >
                    <ChevronRight style={{ width: 48, height: 48 }} className="text-foreground !w-12 !h-12" strokeWidth={3} />
                  </Button>
                ) : (
                  <div className="w-full h-full" />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Fixed Bottom Toolbar - Height based on viewport */}
      {!hideToolbar && (
          <div
            data-scan-row="kb-toolbar"
            className={cn(
              "flex items-stretch justify-center pt-1 pb-1 rounded-lg flex-shrink-0 kb-toolbar-elevated",
              isUIHebrew && "flex-row-reverse"
            )}
            dir="ltr"
            style={{
              gap: 'var(--kb-key-gap)',
              height: 'clamp(48px, 7vh, 64px)',
            }}
          >
            {/* Language toggle button - always neutral/outline style */}
            {isToolbarEnabled("language") && <Button
              variant="outline"
              onClick={handleLanguageButtonClick}
              data-scan-item
              className="flex items-center justify-center px-3 py-2 rounded-xl h-full flex-1"
            >
              <span className="font-medium leading-none whitespace-nowrap" style={{ fontSize: 'calc(clamp(1rem, 2vh, 1.5rem) * var(--font-scale, 1))' }}>{languageToggleLabel}</span>
            </Button>}

            {/* Toolbar items */}
            {toolbarItems.map((item) => {
              const isActive = mode === item.id;
              // When the emoji mode button is clicked, jump cursor to the first
              // emoji so the user doesn't have to navigate back from the toolbar.
              const cursorTarget = item.id === 'emoji' ? 'aac-first-emoji' : undefined;

              return (
                <Button
                  key={item.id}
                  variant={isActive ? "default" : "outline"}
                  onClick={() => toggleMode(item.id as KeyboardMode)}
                  data-scan-item
                  {...(cursorTarget ? { 'data-scan-cursor-target': cursorTarget } : {})}
                  className={cn(
                    "flex items-center justify-center px-3 py-2 rounded-xl h-full flex-1",
                    isActive && "bg-primary text-primary-foreground"
                  )}
                >
                  <span className="font-medium leading-none whitespace-nowrap" style={{ fontSize: 'calc(clamp(1rem, 2vh, 1.5rem) * var(--font-scale, 1))' }}>{item.label}</span>
                </Button>
              );
            })}
          </div>
      )}
      </div>
    </div>
  );
}
